import dayjs from "dayjs";
import { CommonEnums } from "../../enums/common.enums";
import { EnvVariables } from "../../enums/env.variables.enums";
import { LocalStorageHelpers } from "./localstorage.helpers";
import FontsData from "../../@types/fonts.data";

export let getImageUrl = (imageUrl: string) => {
  let baseURL = EnvVariables.apiEndpoint;
  let imageUrlFix = `${baseURL}/${imageUrl}`;

  return imageUrlFix;
};

export let getFileUrlHostedOnApiServer = (imageUrl: string) => {
  let baseURL = EnvVariables.apiEndpoint;
  let imageUrlFix = `${baseURL}/${imageUrl}`;

  return imageUrlFix;
};

export const getFileUrlHostedOnCdn = (storageKey?: string) => {
  if (!storageKey) return "";

  const baseURL = EnvVariables.cdnBaseUrl;

  if (!baseURL) return storageKey;

  return `${baseURL}/${storageKey}`;
};

export const getCdnImageUrl = (storageKey?: string) => {
  if (!storageKey) return "";

  const baseURL = EnvVariables.cdnBaseUrl;

  if (!baseURL) return storageKey;

  return `${baseURL}/${storageKey}`;
};

export const getImageAssetUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";

  if (uploadRecord?.storage_key) {
    return getFileUrlHostedOnCdn(uploadRecord.storage_key);
  }

  if (uploadRecord?.original_file_path) {
    return getFileUrlHostedOnApiServer(uploadRecord.original_file_path);
  }

  return "";
};

export const getDesignTemplateUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";

  if (uploadRecord?.storage_key) {
    return getFileUrlHostedOnCdn(uploadRecord.storage_key);
  }

  if (uploadRecord?.original_file_path) {
    return getFileUrlHostedOnApiServer(uploadRecord.original_file_path);
  }

  return "";
};

export const getAgentProfileImageUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";

  if (uploadRecord.medium_image_url) {
    return getCdnImageUrl(uploadRecord.medium_image_url);
  }
  if (uploadRecord.small_image_url) {
    return getCdnImageUrl(uploadRecord.small_image_url);
  }
  if (uploadRecord.storage_key) {
    return getCdnImageUrl(uploadRecord.storage_key);
  }
  if (uploadRecord.compressed_file_path) {
    return getImageUrl(uploadRecord.compressed_file_path);
  }
  if (uploadRecord.original_file_path) {
    return getImageUrl(uploadRecord.original_file_path);
  }
  return "";
};

/**
 * Load original images assets from CDN or from backend for the design generation
 */
export const loadImageAssetForDesignTool = (uploadRecord: any) => {
  if (!uploadRecord) return "";

  /**
   * storage_key field here holds the original images uploaded on R2
   */
  if (uploadRecord?.storage_key) {
    return getFileUrlHostedOnCdn(uploadRecord.storage_key);
  }

  if (uploadRecord?.extra_large_image_url) {
    return getFileUrlHostedOnCdn(uploadRecord.extra_large_image_url);
  }

  if (uploadRecord?.large_image_url) {
    return getFileUrlHostedOnCdn(uploadRecord.large_image_url);
  }

  if (uploadRecord?.medium_image_url) {
    return getFileUrlHostedOnCdn(uploadRecord.medium_image_url);
  }

  /**
   * original_file_path field here holds the original images uploaded on API Server folders
   */
  if (uploadRecord?.original_file_path) {
    return getFileUrlHostedOnApiServer(uploadRecord?.original_file_path);
  }
  return "";
};

export const getDesignOutputImageUrl = (uploadRecord: any) => {
  const FALLBACK = "/images/square-fallback-image-for-image-preview.png";

  if (!uploadRecord) return FALLBACK;

  if (uploadRecord?.storage_key) {
    const cdnUrl = getCdnImageUrl(uploadRecord.storage_key);
    if (
      cdnUrl &&
      !cdnUrl.includes("undefined") &&
      !cdnUrl.includes("null") &&
      cdnUrl.trim() !== ""
    ) {
      return cdnUrl;
    }
  }

  if (uploadRecord?.original_image_url) {
    const cdnUrl = getCdnImageUrl(uploadRecord.original_image_url);
    if (
      cdnUrl &&
      !cdnUrl.includes("undefined") &&
      !cdnUrl.includes("null") &&
      cdnUrl.trim() !== ""
    ) {
      return cdnUrl;
    }
  }

  if (uploadRecord?.original_image_url) {
    const localUrl = getImageUrl(uploadRecord.original_image_url);
    if (
      localUrl &&
      !localUrl.includes("undefined") &&
      !localUrl.includes("null") &&
      localUrl.trim() !== ""
    ) {
      return localUrl;
    }
  }

  return FALLBACK;
};

export const getAgencyLogoUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";

  if (uploadRecord.storage_key) {
    return getCdnImageUrl(uploadRecord.storage_key);
  }
  if (uploadRecord.original_file_path) {
    return getImageUrl(uploadRecord.original_file_path);
  }
  if (uploadRecord.compressed_file_path) {
    return getImageUrl(uploadRecord.compressed_file_path);
  }
  return "";
};

// export const resolvePropertyVariantImageUrl = (
//   variantKey?: string,
//   fallbackPath?: string,
// ) => {
//   if (variantKey) {
//     return getCdnImageUrl(variantKey);
//   }

//   if (fallbackPath) {
//     return getImageUrl(fallbackPath);
//   }

//   return "";
// };

export const resolvePropertyVariantImageUrl = (
  variantKey?: string,
  fallbackPath?: string,
) => {
  if (variantKey) {
    const cdnUrl = getCdnImageUrl(variantKey);
    if (cdnUrl && !cdnUrl.includes("undefined") && !cdnUrl.includes("null")) {
      return cdnUrl;
    }
  }

  if (fallbackPath) {
    return getImageUrl(fallbackPath);
  }

  return "";
};

export const getPropertyVariantKey = (
  imageObj: any,
  variant: "medium" | "large" | "extra_large" | "original" = "medium",
) => {
  if (!imageObj) return "";

  switch (variant) {
    case "medium":
      return imageObj?.medium_image_url || "";
    case "large":
      return imageObj?.large_image_url || "";
    case "extra_large":
      return imageObj?.extra_large_image_url || "";
    case "original":
      return imageObj?.original_image_url || imageObj?.storage_key || "";
    default:
      return imageObj?.medium_image_url || "";
  }
};

export const resolvePropertyVariantFromObject = (
  imageObj: any,
  variant: "medium" | "large" | "extra_large" | "original" = "medium",
  fallbackPath?: string,
) => {
  const variantKey = getPropertyVariantKey(imageObj, variant);

  if (variantKey) {
    return getCdnImageUrl(variantKey);
  }

  if (fallbackPath) {
    return getImageUrl(fallbackPath);
  }

  return "";
};

export const resolvePropertyImageUrl = (
  storageKey?: string,
  compressedPath?: string,
) => {
  if (storageKey) {
    return getCdnImageUrl(storageKey);
  }

  if (compressedPath) {
    return getImageUrl(compressedPath);
  }

  return "";
};

export let getProfileImageUrl = (imageUrl: string) => {
  if (!imageUrl?.trim()) return "images/profile-placeholder-image.png";

  let baseURL = EnvVariables.apiEndpoint;
  let imageUrlFix = `${baseURL}/${imageUrl}`;

  return imageUrlFix;
};

export let getInvitationCode = () => {
  let userData: any =
    LocalStorageHelpers?.getAgencyData() || LocalStorageHelpers?.getAgentData();
  let baseURL = EnvVariables.appEndpoint;
  let invitation_code = userData?.invitation_code;
  let invitationCodeUrl = `${baseURL}/register?invitation_code=${invitation_code}`;

  return invitationCodeUrl;
};
export let getAddress = ({
  unit,
  no,
  streetName,
  city,
  state,
  country,
  postCode,
}: {
  unit?: string;
  no: string;
  streetName: string;
  city: string;
  state: string;
  country: string;
  postCode: string;
}) => {
  let address = `${streetName}, ${city}, ${state}, ${postCode}`;

  const unitStr = unit ? unit.toString().trim() : "";
  const numberStr = no ? no.toString().trim() : "";

  if (unitStr && numberStr) {
    address = `${unitStr}/${numberStr} ${address}`;
  } else if (numberStr) {
    address = `${numberStr} ${address}`;
  } else if (unitStr) {
    address = `${unitStr} ${address}`;
  }

  return address;
};

export let getAgentName = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  let agentName = "";

  if (userData?.role === CommonEnums?.userRole?.agent) {
    agentName = `${userData?.principal_agent_first_name} ${userData?.principal_agent_last_name}`;
  } else {
    agentName = `${userData?.principal_agent_id?.principal_agent_first_name} ${userData?.principal_agent_id?.principal_agent_last_name}`;
  }

  return agentName;
};

export let getAgentProfileImg = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  let agentImg = "";

  if (userData?.role === CommonEnums?.userRole?.agent) {
    agentImg = `${userData?.profile_image}`;
  } else {
    agentImg = `${userData?.principal_agent_id?.profile_image_ref?.compressed_file_path}`;
  }
  return getImageUrl(agentImg);
};

export let getAgentId = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  let agentId = "";

  if (userData?.role === CommonEnums?.userRole?.agent) {
    agentId = userData?._id;
  } else {
    agentId = userData?.principal_agent_id?._id;
  }

  return agentId;
};

export let getAgencyId = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  let agencyId = "";

  if (userData?.role === CommonEnums?.userRole?.agent) {
    agencyId = userData?.agency_ref?._id;
  } else {
    agencyId = userData?._id;
  }

  return agencyId;
};

export let isAgency = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  let is_agency = false;

  if (userData?.role === CommonEnums?.userRole?.agency) {
    is_agency = true;
  } else {
    is_agency = false;
  }

  return is_agency;
};

export let isAgent = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  return userData?.role === CommonEnums?.userRole?.agent;
};

export let getLoggedInUserId = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  return userData?._id || "";
};

export let agentApproved = () => {
  let userData: any =
    LocalStorageHelpers?.getAgentData() || LocalStorageHelpers?.getAgencyData();

  let statue_check = true;

  if (userData?.role === CommonEnums?.userRole?.agent) {
    if (
      userData?.registration_status ===
      CommonEnums?.inviteStatus?.waiting_for_registration_approval?.key
    ) {
      statue_check = false;
    } else {
      statue_check = true;
    }
  } else {
    statue_check = true;
  }

  return statue_check;
};

export const agencyApproved = () => {
  const agencyData: any = LocalStorageHelpers?.getAgencyData();

  let status_check = true;

  if (agencyData?.status === CommonEnums?.status?.pending_for_admin_approval) {
    status_check = false;
  } else {
    status_check = true;
  }

  return status_check;
};

export let editPermissionCheck = (approval_status: string) => {
  let is_edit = false;
  switch (approval_status) {
    case CommonEnums.approvalStatus?.pending?.key:
      is_edit = true;
      break;
  }
  return is_edit;
};

export const rootCssSet = () => {
  let root = document.getElementById("root");

  if (root?.classList.contains("root-overflow-hidden")) {
    root?.classList.remove("root-overflow-hidden");
  } else {
    root?.classList.add("root-overflow-hidden");
  }
};

export const CLIENT_DETAILS_DATETIME_FORMAT = "h:mm A, D MMM YYYY";

export const formatDetailsDateTime = (value?: string | Date | null) => {
  return value ? dayjs(value).format(CLIENT_DETAILS_DATETIME_FORMAT) : "N/A";
};

/**
 * @param {FontsData} fontData
 * @returns {string | undefined}
 */

export const getFontUrl = (fontData: FontsData) => {
  const cdnUrl = EnvVariables.cdnBaseUrl;
  let apiURL = EnvVariables.apiEndpoint;

  if (fontData.woff2_font_storage_key) {
    return `${cdnUrl}/${fontData.woff2_font_storage_key}`;
  }
  if (fontData.woff2_font_file_url) {
    return `${apiURL}/${fontData.woff2_font_file_url}`;
  }
  if (fontData.original_font_file_url) {
    return `${apiURL}/${fontData.original_font_file_url}`;
  }
};

export const getAgencyLogoForDesignTool = ({
  agencyData,
  brandData,
  useAgencyOfficeLogo = false,
}: {
  agencyData: any;
  brandData: any;
  useAgencyOfficeLogo: boolean;
}) => {
  if (brandData && !useAgencyOfficeLogo) {
    // console.log("returned logo > brand");
    return loadImageAssetForDesignTool(brandData?.brand_logo);
  }

  // console.log("returned logo > agency");

  return loadImageAssetForDesignTool(agencyData?.profile_image_ref);
};
